/**
 * @voltxampere/web — Custom shortcut overrides store (Sprint 23 D1)
 *
 * BLUEPRINT §13.4 Customization (Faz 2+) format'ı:
 *
 *   { "shortcuts": { "file.save": ["Mod+s", "Mod+w"] } }
 *
 * Override semantiği "replace" (ADR-0047 §"Shortcut override
 * format" closure'ı): mevcut default key'leri tamamen değiştirir.
 * "Varsayılana dön" override'ı silerek default'a döner. Empty
 * array `[]` özel: command için hiç shortcut yok (disable).
 *
 * State: `shortcutOverrides` signal — `Record<commandId, string[]>`.
 * IDB hidrate boot'ta (initShortcutOverrides), persist
 * fire-and-forget (settingsStore.mergeSettings).
 *
 * Public API:
 *
 *  - initShortcutOverrides()   — IDB read → seed signal (idempotent)
 *  - getEffectiveKeys(id)      — built-in default veya override
 *  - setOverride(id, keys)     — write override + persist + apply
 *                                 (ShortcutManager re-bind)
 *  - clearOverride(id)         — override sil + default'a dön
 *  - resetAllOverrides()       — hepsini sil + default'lara dön
 *  - detectConflict(keys, id)  — aynı key combo başka command'a
 *                                 atanmış mı? Tested in tests.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import { getSettings, mergeSettings } from "@voltxampere/persistence";

/** signal — current overrides keyed by command id. */
export const shortcutOverrides = signal<Record<string, readonly string[]>>({});

let initialised = false;

/**
 * Read settings.shortcuts from IDB and seed the signal. Call once
 * at boot (main.ts). Idempotent.
 */
export async function initShortcutOverrides(): Promise<number> {
  if (initialised) return Object.keys(shortcutOverrides.value).length;
  initialised = true;
  try {
    const settings = await getSettings();
    if (settings?.shortcuts) {
      shortcutOverrides.value = { ...settings.shortcuts };
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA shortcuts] hydrate failed:", err);
    }
  }
  return Object.keys(shortcutOverrides.value).length;
}

/**
 * Apply an override. Persists to IDB fire-and-forget.
 * Empty `keys` array is a valid "disable command" override.
 */
export function setOverride(commandId: string, keys: readonly string[]): void {
  const next = { ...shortcutOverrides.value, [commandId]: [...keys] };
  shortcutOverrides.value = next;
  void mergeSettings({ shortcuts: next }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA shortcuts] persist failed:", err);
    }
  });
}

/** Remove an override → command falls back to default keys. */
export function clearOverride(commandId: string): void {
  const next = { ...shortcutOverrides.value };
  // Reflect.deleteProperty is the no-op-on-missing path; explicit
  // destructure keeps TS strict happy.
  if (commandId in next) {
    const rest: Record<string, readonly string[]> = {};
    for (const [k, v] of Object.entries(next)) {
      if (k !== commandId) rest[k] = v;
    }
    shortcutOverrides.value = rest;
    void mergeSettings({ shortcuts: rest }).catch((err: unknown) => {
      if (import.meta.env.DEV) {
        console.warn("[VXA shortcuts] persist failed:", err);
      }
    });
  }
}

/** Reset every override globally. Confirmation dialog UI side. */
export function resetAllOverrides(): void {
  shortcutOverrides.value = {};
  void mergeSettings({ shortcuts: {} }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA shortcuts] persist failed:", err);
    }
  });
}

/**
 * Detect if `keys` collide with another command's effective key
 * combo. Used by AYARLAR > Klavye edit UI to warn before saving.
 *
 * Returns the conflicting commandId, or null if no collision.
 *
 * `excludeCommandId` lets the caller skip self-check (a command
 * keeping its existing key is not a conflict with itself).
 */
export function detectConflict(
  keys: readonly string[],
  excludeCommandId: string,
  effectiveKeyMap: Readonly<Record<string, readonly string[]>>,
): string | null {
  if (keys.length === 0) return null;
  for (const [otherId, otherKeys] of Object.entries(effectiveKeyMap)) {
    if (otherId === excludeCommandId) continue;
    for (const k of keys) {
      if (otherKeys.includes(k)) return otherId;
    }
  }
  return null;
}

export function __resetShortcutOverridesForTests(): void {
  shortcutOverrides.value = {};
  initialised = false;
}
